import { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  IconButton,
  Paper,
  Grid,
  Box,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import { ApiServices } from "../../../utils/ApiService";

export default function BankBranchesPage() {
  // States
  const [banks, setBanks] = useState([]);
  const [isBankChanged, setIsBankChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bankNames, setBankNames] = useState([]);
  const [bankTab, setBankTab] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const userId = JSON.parse(localStorage.getItem("user"))?.id;

  // Load initial data
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    setBanks(user.bankBranches || []);
    const bankTabData = user.bank_tab || [];
    setBankTab(bankTabData);
    setBankNames(bankTabData.map((b) => b.bank_name));
  }, []);

  const bankBranchColumns = [
    {
      field: "bank_name",
      headerName: "Bank Name",
      flex: 1,
      editable: true,
      renderEditCell: (params) => (
        <Select
          value={params.value || ""}
          onChange={(e) =>
            params.api.setEditCellValue(
              {
                id: params.id,
                field: params.field,
                value: e.target.value,
              },
              e
            )
          }
          fullWidth
        >
          {bankNames.map((name, idx) => (
            <MenuItem key={idx} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    { field: "branch_name", headerName: "Branch", flex: 1, editable: true },
    { field: "phone_no", headerName: "Phone No.", flex: 1, editable: true },
    { field: "email", headerName: "Email", flex: 1, editable: true },
    { field: "address", headerName: "Address", flex: 1, editable: true },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDeleteBank(params.id)}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  // Add bank
  const handleAddBankBranch = () => {
    const newId = banks.length ? Math.max(...banks.map((b) => b.id || 0)) + 1 : 1;
    const defaultBank = bankNames[0] || "";

    setBanks((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        bank_name: defaultBank,
        branch_name: "",
        phone_no: "",
        email: "",
        address: "",
      },
    ]);
    setIsBankChanged(true);
  };

  // Delete bank
  const handleDeleteBank = async (id) => {
    if (!window.confirm("Are you sure you want to delete this bank?")) return;
    try {
      setLoading(true);
      const res = await ApiServices.post("user/delete-bank-branch", {
        id,
        deleted_by: userId,
      });
      if (res?.status === 200) {
        setBanks(res.data.bankBranches);
        setIsBankChanged(false);
        enqueueSnackbar("Bank branch deleted successfully!", { variant: "success" });
      } else {
        enqueueSnackbar("Bank branch not deleted!", { variant: "error" });
      }
    } catch (error) {
      console.error("Delete error:", error);
      enqueueSnackbar("Error while deleting bank branch.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Save bank branches
  const handleSaveBankBranches = async () => {
    try {
      setLoading(true);

      const payload = banks.map((branch) => {
        const isNew =
          typeof branch.id === "string" && branch.id.startsWith("temp");
        return {
          ...branch,
          id: isNew ? null : branch.id,
          bank_id:
            bankTab.find((b) => b.bank_name === branch.bank_name)?.id ||
            branch.bank_id ||
            null,
        };
      });

      const res = await ApiServices.post("user/modify-bank-branches", payload);
      if (res?.status === 200) {
        updateBankBranchesInLocal(res.data.bankBranches || []);
        enqueueSnackbar("Bank branches modified successfully!", {
          variant: "success"
        });
        setIsBankChanged(false);
      } else {
        enqueueSnackbar("Failed to modify bank branches!", { variant: "error" });
      }
    } catch (error) {
      console.error("Save error:", error);
      enqueueSnackbar("Error saving bank branches.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const updateBankBranchesInLocal = (bankBranches) => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    user.bankBranches = bankBranches || [];
    localStorage.setItem("user", JSON.stringify(user));
    setBanks(bankBranches);
  };

  // Process row update
  const processBankUpdate = useCallback(
    (newRow, oldRow) => {
      const isModified =
        newRow.bank_name !== oldRow.bank_name ||
        newRow.branch_name !== oldRow.branch_name ||
        newRow.phone_no !== oldRow.phone_no ||
        newRow.email !== oldRow.email ||
        newRow.address !== oldRow.address;

      if (isModified) setIsBankChanged(true);

      setBanks((prev) =>
        prev.map((bank) => (bank.id === newRow.id ? { ...bank, ...newRow } : bank))
      );

      return newRow;
    },
    []
  );

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Bank Branches</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBankBranch}
          >
            Add
          </Button>
          <Button
            sx={{ ml: 2 }}
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveBankBranches}
            disabled={!isBankChanged}
          >
            Save
          </Button>
        </Box>
      </Grid>

      <div style={{ height: 400, marginTop: 16 }}>
        <DataGrid
          rows={banks}
          columns={bankBranchColumns}
          editMode="row"
          processRowUpdate={processBankUpdate}
          pageSize={5}
          rowsPerPageOptions={[5]}
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
        />
      </div>
    </Paper>
  );
}
